import {CollectionViewer, SelectionChange, DataSource} from '@angular/cdk/collections';
import {FlatTreeControl} from '@angular/cdk/tree';
import {NgClass, NgIf, DatePipe} from '@angular/common';
import {Component, ElementRef, EventEmitter, Injectable, Input, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import {BehaviorSubject, merge, Observable, Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import { MatTreeModule } from '@angular/material/tree';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import { SpToolsModule } from "../../modules/sp-tools/sp-tools-module";
import { SelectionModel } from '@angular/cdk/collections';
import { TreeSearchService } from '../../services/tree-search/tree-search.service';

/** Flat node with expandable and level information */
export class DynamicFlatNode {
  constructor(
    public id: number,
    public name: string,
    public level = 0,
    public expandable = false,
    public isLoading = false,
    public created_at?: string
  ) {}
}

/**
 * Database for dynamic data. When expanding a node in the tree, the data source will need to fetch
 * the descendants data from the database.
 */
@Injectable({providedIn: 'root'})
export class DynamicDatabase {

  constructor(
    private getChildrenDep: (nodeId: number | null, categoryPage?: number, categoryPerPage?: number, presetPage?: number, presetPerPage?: number) => Promise<{ id: number, name: string, created_at?: string }[]>,
    private isNodeExpandable: (node: any) => boolean) {}

  async getChildren(nodeId: number): Promise<DynamicFlatNode[]> {
    // Default to page 1 and 15 items per page for both category and preset pagination
    const childElements = await this.getChildrenDep(nodeId, 1, 100, 1, 100);
    return childElements.map(child => 
      new DynamicFlatNode(child.id, child.name, 0, this.isNodeExpandable(child), false, child.created_at)
    );
  }
}

/**
 * File database, it can build a tree structured Json object from string.
 * Each node in Json object represents a file or a directory. For a file, it has filename and type.
 * For a directory, it has filename and children (a list of files or directories).
 * The input will be a json object string, and the output is a list of `FileNode` with nested
 * structure.
 */
export class DynamicDataSource implements DataSource<DynamicFlatNode> {
  dataChange = new BehaviorSubject<DynamicFlatNode[]>([]);
  private expansionSubscription: Subscription;
  //TODO: Shouldn't be there 
  private treeComponent: TreeComponent = null; // Add reference to TreeComponent

  get data(): DynamicFlatNode[] {
    return this.dataChange.value;
  }
  set data(value: DynamicFlatNode[]) {
    this._treeControl.dataNodes = value;
    this.dataChange.next(value);
  }

  constructor(
    private _treeControl: FlatTreeControl<DynamicFlatNode>,
    private _database: DynamicDatabase,
  ) {}

  // Method to set the tree component reference
  setTreeComponent(component: TreeComponent): void {
    this.treeComponent = component;
  }

  // Method to append data to existing data
  appendData(newNodes: DynamicFlatNode[]): void {
    // Just append the new nodes
    this.data = [...this.data, ...newNodes];
  }

  connect(collectionViewer: CollectionViewer): Observable<DynamicFlatNode[]> {
    // Clean up any existing subscription
    if (this.expansionSubscription) {
      this.expansionSubscription.unsubscribe();
    }
    
    // Create new subscription
    this.expansionSubscription = this._treeControl.expansionModel.changed.subscribe(change => {
      if (
        (change as SelectionChange<DynamicFlatNode>).added ||
        (change as SelectionChange<DynamicFlatNode>).removed
      ) {
        this.handleTreeControl(change as SelectionChange<DynamicFlatNode>);
      }
    });

    return merge(collectionViewer.viewChange, this.dataChange).pipe(map(() => this.data));
  }

  disconnect(collectionViewer: CollectionViewer): void {
    // Clean up the subscription
    if (this.expansionSubscription) {
      this.expansionSubscription.unsubscribe();
    }
  }

  /** Handle expand/collapse behaviors */
  handleTreeControl(change: SelectionChange<DynamicFlatNode>) {
    if (change.added) {
      change.added.forEach(node => this.toggleNode(node, true));
    }
    if (change.removed) {
      change.removed
        .slice()
        .reverse()
        .forEach(node => this.toggleNode(node, false));
    }
  }

  /**
   * Toggle the node, removing its children if collapsing, or inserting new nodes when expanding.
   */
  async toggleNode(node: DynamicFlatNode, expand: boolean) {
    // Only show loading state when expanding, not when collapsing
    if (expand) {
      node.isLoading = true;
    }
    
    // When collapsing, we don't need to fetch children, just close immediately
    if (!expand) {
      const index = this.data.indexOf(node);
      if (index >= 0) {
        let count = 0;
        for (let i = index + 1; i < this.data.length && this.data[i].level > node.level; i++, count++) {}
        this.data.splice(index + 1, count);
        this.dataChange.next(this.data);
      }
      return;
    }
    
    // For expanding nodes, fetch children as usual
    const children = await this._database.getChildren(node.id);
    const index = this.data.indexOf(node);
    if (!children || index < 0) {
      node.isLoading = false;
      return;
    }

    // Create and add the new nodes in a single operation
    const nodes = children.map(child =>
      new DynamicFlatNode(child.id, child.name, node.level + 1, child.expandable, false, child.created_at)
    );
    
    // Add a "Load more" node if necessary (if we have a full page of results)
    if (children.length >= 15) { // Assuming 15 is the page size for expanding nodes
      const loadMoreNode = new DynamicFlatNode(-1, 'load-more', node.level + 1, false, false);
      loadMoreNode['parent'] = node.name;
      loadMoreNode['parentId'] = node.id;
      loadMoreNode['nextPage'] = 2; // Initial next page is 2 for presets
      nodes.push(loadMoreNode);
    }
    
    // Update the data and finish loading in one change detection cycle
    this.data.splice(index + 1, 0, ...nodes);
    this.dataChange.next(this.data);

    this.treeComponent?.recoverSelectedNode();
    node.isLoading = false;
  }
}

/**
 * @title Tree with dynamic data
 */
@Component({
  selector: 'tree-component',
  templateUrl: 'tree-component.html',
  styleUrls: ['tree-component.scss'],
  standalone: true,
  imports: [
    MatTreeModule,
    MatIconModule,
    MatProgressBarModule,
    MatButtonModule,
    NgIf,
    MatProgressSpinnerModule,
    NgClass,
    SpToolsModule,
    DatePipe
  ]
})

export class TreeComponent implements OnInit, OnDestroy {
  @Input() getChildrenDep: (nodeId: number | null, categoryPage?: number, categoryPerPage?: number, presetPage?: number, presetPerPage?: number) => Promise<{ id: number, name: string, created_at?: string }[]>;
  @Input() searchChildrenDep: (page: number, perPage: number, searchText: string) => Promise<{ id: number, name: string, created_at?: string }[]>;
  @Input() isNodeExpandable: (node: any) => boolean;
  
  // Add property to track selected category/parent node
  selectedLeftSideNode: { id: number, name: string };
 
  // Component should be able to control the loading state of the tree component and the no search results state
  isTreeComponentLoading = false;
  hasNoSearchResults = false;
  searchText: string = '';

  // Pagination state
  public currentPage: number = 1;
  private itemsPerPage: number = 100;
  hasMoreItems: boolean = true; // Default to true to check on first load
  isLoadingMore: boolean = false;
  private totalItems: number = 0;
  private originalData: DynamicFlatNode[] = [];
  // Flag to determine if we should use backend pagination
  private useBackendPagination: boolean = false;

  @Output() nodeSelected: EventEmitter<any> = new EventEmitter<any>();
  
  // Reference to the scroll container
  @ViewChild('treeScrollContainer') scrollContainer: ElementRef;

  treeControl: FlatTreeControl<DynamicFlatNode>;
  selectionModel = new SelectionModel<DynamicFlatNode>(false); // single selection model
  selectedNodeId: number = null; // Add this to track selected node ID

  dataSource: DynamicDataSource;
  database: DynamicDatabase;
  
  private searchSubscription: Subscription;

  constructor(private treeSearchService: TreeSearchService) {
    this.treeControl = new FlatTreeControl<DynamicFlatNode>(this.getLevel, this.isExpandable);
    
    // Initialize searchText with the current value from the service
    this.searchText = this.treeSearchService.getCurrentSearchText();
  }

  async ngOnInit(): Promise<void> {
    this.isTreeComponentLoading = true;
    
    try {
      // Initialize database and data source
      this.database = new DynamicDatabase(this.getChildrenDep, this.isNodeExpandable);
      this.dataSource = new DynamicDataSource(this.treeControl, this.database);
      
      // Enable pagination if we have searchChildrenDep available
      this.useBackendPagination = !!this.searchChildrenDep;
      
      await this.loadInitialData();
      
      // Subscribe to search text changes
      this.searchSubscription = this.treeSearchService.searchText$.subscribe(searchText => {
        if (searchText !== this.searchText) {
          this.searchText = searchText;
          this.onSearchTextChange(searchText);
        }
      });
    } finally {
      this.dataSource.setTreeComponent(this);
      this.isTreeComponentLoading = false;
    }
  }
  
  private async loadInitialData(): Promise<void> {
    // Get the current search text from the service
    const currentSearchText = this.treeSearchService.getCurrentSearchText();
    this.searchText = currentSearchText;
    
    // Load data with search applied if we have a search string
    if (currentSearchText && currentSearchText.trim() !== '') {
      const searchResults = await this.searchChildrenDep(1, this.itemsPerPage, currentSearchText);
      this.originalData = this.createFlatNodesFromData(searchResults);
    } else {
      // Otherwise load root elements using getChildrenDep with null nodeId
      const rootElements = await this.getChildrenDep(null, 1, this.itemsPerPage, 1, 100);
      this.originalData = this.createFlatNodesFromData(rootElements);
      
      // Try to extract selected category from parent node
      // The parent node information might be available in the first node
      if (this.originalData && this.originalData.length > 0) {
        const firstNode = this.originalData[0];
        // If we have parentId information in the data, use it
        if (firstNode && firstNode['parentId']) {
          this.selectedLeftSideNode = {
            id: firstNode['parentId'],
            name: firstNode['parent'] || 'Category'
          };
        }
      }
    }
    
    this.totalItems = this.originalData.length;
    
    // Always set all data to dataSource (no client-side pagination)
    this.dataSource.data = this.originalData;
    
    // Reset pagination state
    this.currentPage = 1;
    
    // Set hasMoreItems flag only if using backend pagination
    if (this.useBackendPagination) {
      this.hasMoreItems = this.originalData.length >= this.itemsPerPage;
    } else {
      this.hasMoreItems = false; // No more items to load when not using backend pagination
    }
  }
  
  private createFlatNodesFromData(data: any[]): DynamicFlatNode[] {
    return data.map(item => new DynamicFlatNode(
      item.id, 
      item.name, 
      0, 
      this.isNodeExpandable(item),
      false,
      item.created_at
    ));
  }
  
  ngOnDestroy(): void {
    if (this.dataSource) {
      this.dataSource.disconnect(null);
    }
    
    if (this.searchSubscription) {
      this.searchSubscription.unsubscribe();
    }
  }


  public async refreshTree(): Promise<void> {
    this.isTreeComponentLoading = true;
    this.currentPage = 1; // Reset pagination when refreshing tree
    
    // Only use hasMoreItems with backend pagination
    if (this.useBackendPagination) {
      this.hasMoreItems = true; // Default to true for backend pagination
    } else {
      this.hasMoreItems = false;
    }

    // Get the current search text from the service
    const searchText = this.treeSearchService.getCurrentSearchText();

    if (searchText.trim() !== '') {
      await this.loadSearchResults(searchText);
    } else {
      await this.loadStandardData();
    }

    this.recoverSelectedNode();
    this.isTreeComponentLoading = false;
  }
  
  private async loadSearchResults(query: string): Promise<void> {
    try {
      // Try to use searchChildrenDep with pagination parameters
      const searchResults = await this.searchChildrenDep(this.currentPage, this.itemsPerPage, query);
      
      // Create nodes from search results
      this.originalData = this.createFlatNodesFromData(searchResults);
      
      this.totalItems = this.originalData.length;
      this.hasNoSearchResults = this.originalData.length === 0;
      
      // Always show all data (no client-side pagination)
      this.dataSource.data = this.originalData;
      
      // Set hasMoreItems flag only if using backend pagination
      if (this.useBackendPagination) {
        // Fix: Only set hasMoreItems to true if we have exactly itemsPerPage items
        // If we have fewer, we know there are no more items
        this.hasMoreItems = this.originalData.length >= this.itemsPerPage;
        
        // If total items is less than itemsPerPage, we know there are no more items
        if (this.totalItems < this.itemsPerPage) {
          this.hasMoreItems = false;
        }
      } else {
        this.hasMoreItems = false;
      }
    } catch (error) {
      // Use default page and perPage values
      const searchResults = await this.searchChildrenDep(1, this.itemsPerPage, query);
      
      // Create nodes from search results
      this.originalData = this.createFlatNodesFromData(searchResults);
      
      this.totalItems = this.originalData.length;
      this.hasNoSearchResults = this.originalData.length === 0;
      
      // Always show all data (no client-side pagination)
      this.dataSource.data = this.originalData;
      
      // Since we couldn't use pagination parameters, disable hasMoreItems
      this.hasMoreItems = false;
    }
  }
  
  private async loadStandardData(): Promise<void> {
    // Reset pagination
    this.currentPage = 1;
    
    // Load initial data using getChildrenDep with null or 0 as the node ID
    const rootElements = await this.getChildrenDep(null, 1, this.itemsPerPage, 1, 100);
    this.originalData = this.createFlatNodesFromData(rootElements);
    this.totalItems = this.originalData.length;
    
    // Always show all data (no client-side pagination)
    this.dataSource.data = this.originalData;
    
    // Set hasMoreItems flag only if using backend pagination
    if (this.useBackendPagination) {
      // Fix: Only set hasMoreItems to true if we have exactly itemsPerPage items
      // If we have fewer, we know there are no more items to load
      this.hasMoreItems = this.originalData.length >= this.itemsPerPage;
      
      // If total items is less than itemsPerPage, we know there are no more items
      if (this.totalItems < this.itemsPerPage) {
        this.hasMoreItems = false;
      }
    } else {
      this.hasMoreItems = false;
    }
  }

 
  onScroll(event: Event): void {
    // Only handle scroll events if using backend pagination
    if (!this.useBackendPagination || !this.hasMoreItems || this.isLoadingMore) {
      return;
    }

    const element = event.target as HTMLElement;
    
    // Calculate if we're near the bottom (within 50px)
    const isNearBottom = element.scrollHeight - element.scrollTop - element.clientHeight < 50;
    
    if (isNearBottom) {
      this.loadMoreItems();
    }
  }

  private async loadMoreItems(): Promise<void> {
    // Only load more items if using backend pagination
    if (!this.useBackendPagination || this.isLoadingMore || !this.hasMoreItems) {
      return;
    }

    this.isLoadingMore = true;
    
    try {
      this.currentPage++;
      await this.loadMoreFromBackend();
    } finally {
      this.isLoadingMore = false;
    }
  }
  
  private async loadMoreFromBackend(): Promise<void> {
    // Try to use searchChildrenDep first if we have search text
    if (this.searchText && this.searchText.trim() !== '') {
      try {
        // Use searchChildrenDep with pagination for search results
        const results = await this.searchChildrenDep(this.currentPage, this.itemsPerPage, this.searchText);
        
        if (!results || results.length === 0) {
          this.hasMoreItems = false;
          return;
        }
        
        // Set hasMoreItems based on results count
        this.hasMoreItems = results.length >= this.itemsPerPage;
        
        // Convert results to nodes and append
        const newNodes = this.createFlatNodesFromData(results);
        
        // Filter out any nodes that already exist in the data source to prevent duplicates
        const existingIds = this.dataSource.data.map(node => node.id);
        const uniqueNewNodes = newNodes.filter(node => !existingIds.includes(node.id));
        
        if (uniqueNewNodes.length === 0) {
          // If all returned nodes are duplicates, there are no more unique items
          this.hasMoreItems = false;
          return;
        }
        
        if (uniqueNewNodes.length > 0) {
          this.dataSource.appendData(uniqueNewNodes);
        }
        
        return;
      } catch (error) {
        console.log('searchChildrenDep pagination error', error);
        this.hasMoreItems = false;
        return;
      }
    } else {
      // No search text, we're loading more root elements from the selected category
      await this.loadMoreRootElements();
    }
  }

  /**
   * Handles loading more root elements when scrolling the main view (not search)
   */
  private async loadMoreRootElements(): Promise<void> {
    try {
      // For the main tree, we load more root elements or more children from the selected category
      const nodeId = this.selectedLeftSideNode?.id || null;
      
      // Use getChildrenDep with appropriate pagination parameters
      const results = await this.getChildrenDep(
        nodeId,
        this.currentPage, // Use currentPage for category pagination when scrolling
        this.itemsPerPage, // Use itemsPerPage for category page size
        1, // Keep preset page fixed at 1
        100 // Default preset page size
      );
      
      if (!results || results.length === 0) {
        this.hasMoreItems = false;
        return;
      }
      
      // Set hasMoreItems based on results count
      this.hasMoreItems = results.length >= this.itemsPerPage;
      
      // Convert results to nodes and append
      const newNodes = this.createFlatNodesFromData(results);
      
      // Filter out any nodes that already exist in the data source to prevent duplicates
      const existingIds = this.dataSource.data.map(node => node.id);
      const uniqueNewNodes = newNodes.filter(node => !existingIds.includes(node.id));
      
      if (uniqueNewNodes.length === 0) {
        // If all returned nodes are duplicates, there are no more unique items
        this.hasMoreItems = false;
        return;
      }
      
      if (uniqueNewNodes.length > 0) {
        this.dataSource.appendData(uniqueNewNodes);
      }
    } catch (error) {
      console.log('Root elements pagination error', error);
      this.hasMoreItems = false;
    }
  }

  getLevel = (node: DynamicFlatNode) => node.level;

  isExpandable = (node: DynamicFlatNode) => node.expandable;

  hasChild = (_: number, _nodeData: DynamicFlatNode) => _nodeData.expandable;

  onNodeClick(node: any): void {
    this.selectNodeById(node.id);
    this.nodeSelected.emit(node);
  }
  
  public clearNodeSelection(): void {
    this.selectionModel.clear();
    this.selectedNodeId = null; // Clear the selected node ID
  }

  public selectNodeById(nodeId: number): void {
    this.selectionModel.clear();

    const node = this.dataSource?.data.find(n => n.id === nodeId);
    if (node) {
      this.selectionModel.select(node);
      this.selectedNodeId = node.id;
    }
  }

  public recoverSelectedNode(): void {
    if (this.selectedNodeId) {
      this.selectNodeById(this.selectedNodeId);
    }
  }

  isSelected(node: DynamicFlatNode): boolean {
    return this.selectionModel.isSelected(node);
  }

  // Method to determine if a node is a "Load more" node
  isLoadMore = (_: number, node: DynamicFlatNode): boolean => {
    return node.name === 'load-more';
  }

  // Method to handle clicks on the "Load more" node
  loadOnClick(event: MouseEvent, node: DynamicFlatNode): void {
    event.stopPropagation();
    if (node.name === 'load-more' && node['parentId']) {
      this.loadMoreChildren(node['parentId'], node);
    }
  }

  // Method to handle keyboard events on the "Load more" node
  loadOnKeypress(event: KeyboardEvent, node: DynamicFlatNode): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (node.name === 'load-more' && node['parentId']) {
        this.loadMoreChildren(node['parentId'], node);
      }
    }
  }

  // Method to load more children for a specific parent node
  private async loadMoreChildren(parentId: number, loadMoreNode: DynamicFlatNode): Promise<void> {
    if (!parentId || this.isLoadingMore) {
      return;
    }

    this.isLoadingMore = true;
    loadMoreNode.isLoading = true;
    
    try {
      // Get the next page from the load more node
      const nextPage = loadMoreNode['nextPage'] || 2;
      
      // Fetch more children for this specific parent using getChildrenDep
      // When clicking "Load more presets", we keep categoryPage fixed at 1
      // and increment presetPage instead
      const moreChildren = await this.getChildrenDep(
        parentId,
        1, // Keep category page fixed at 1
        100, // Default category page size
        nextPage, // Increment preset page
        100 // Default preset page size
      );
      
      if (!moreChildren || moreChildren.length === 0) {
        console.log(`No more presets found for category ${parentId}`);
        // No more children to load, remove the "Load more" node
        const index = this.dataSource.data.indexOf(loadMoreNode);
        if (index > -1) {
          this.dataSource.data.splice(index, 1);
          this.dataSource.data = [...this.dataSource.data];
        }
        return;
      }
      
      // Convert to flat nodes
      const newNodes = moreChildren.map(child => 
        new DynamicFlatNode(child.id, child.name, loadMoreNode.level, this.isNodeExpandable(child), false, child.created_at)
      );
      
      // Get the index of the "Load more" node
      const loadMoreIndex = this.dataSource.data.indexOf(loadMoreNode);
      
      if (loadMoreIndex > -1) {
        // Remove the current "Load more" node
        this.dataSource.data.splice(loadMoreIndex, 1);
        
        // Insert the new nodes at the position where the "Load more" node was
        this.dataSource.data.splice(loadMoreIndex, 0, ...newNodes);
        
        // If there might be more children, add a new "Load more" node with incremented page
        if (moreChildren.length >= 15) {
          const newLoadMoreNode = new DynamicFlatNode(-1, 'load-more', loadMoreNode.level, false, false);
          newLoadMoreNode['parent'] = loadMoreNode['parent'];
          newLoadMoreNode['parentId'] = parentId;
          newLoadMoreNode['nextPage'] = nextPage + 1; // Increment the page for next load
          
          // Add the new "Load more" node after the inserted children
          this.dataSource.data.splice(loadMoreIndex + newNodes.length, 0, newLoadMoreNode);
        }
        
        // Update the data source
        this.dataSource.data = [...this.dataSource.data];
      }
    } finally {
      this.isLoadingMore = false;
      loadMoreNode.isLoading = false;
    }
  }

  public async onSearchTextChange(query: string): Promise<void> {
    this.isTreeComponentLoading = true;
    this.currentPage = 1;
    
    // Only set hasMoreItems for backend pagination
    if (this.useBackendPagination) {
      this.hasMoreItems = true;
    } else {
      this.hasMoreItems = false;
    }
    
    try {
      // Store the search query for future use
      this.searchText = query;
      
      // Update the search service
      this.treeSearchService.updateSearchText(query);
      
      // Fetch data with search filter
      await this.loadSearchResults(query);
      
      if (this.dataSource) {
        this.recoverSelectedNode();
      }
    } catch (error) {
      this.hasNoSearchResults = true;
    } finally {
      this.isTreeComponentLoading = false;
    }
  }
}
